<template>
    <v-form>
        <v-row dense class="mb-4">
            <v-col>
                <LocaleSwitcher @change="changeActiveLangCode" />
            </v-col>
        </v-row>
        <v-row v-if="loadingDescriptions" dense>
            <v-sheet class="full-width d-flex justify-center pb-8">
                <v-progress-linear color="primary" indeterminate class="mb-0" height="3px" />
            </v-sheet>
        </v-row>
        <v-row v-else dense>
            <v-col cols="2">
                <span class="text-h6 text--secondary">
                    {{ $t('description') }}
                </span>
            </v-col>
            <v-col cols="12" md="10" class="px-2">
                <v-row v-if="isShowEditors" no-gutters>
                    <v-col v-if="serviceType !== 'ownVisa'" cols="12" class="mb-2">
                        <NewHtmlEditor
                            ref="editorDescriptionShort"
                            v-model="descriptionShortLocalized"
                            :label="`${$t('description_type.short')}`"
                            class="mb-2"
                            clear-paste-html
                        >
                            <div
                                v-if="selectedLocale"
                                class="fi fill-height px-3"
                                :class="[`fi-${useFlagIconCode(selectedLocale)}`]"
                            />
                        </NewHtmlEditor>
                    </v-col>
                    <v-col cols="12" class="mb-2">
                        <NewHtmlEditor
                            ref="editorDescriptionFull"
                            v-model="descriptionLocalized"
                            :label="`${$t('description_type.full')}`"
                            class="mb-2"
                        >
                            <div
                                v-if="selectedLocale"
                                class="fi fill-height px-3"
                                :class="[`fi-${useFlagIconCode(selectedLocale)}`]"
                            />
                        </NewHtmlEditor>
                    </v-col>
                    <v-col
                        v-if="serviceType !== 'customComplexTour' && serviceType !== 'ownVisa'"
                        cols="12"
                        class="mb-2"
                    >
                        <NewHtmlEditor
                            ref="editorImportantInfo"
                            v-model="importantInfoLocalized"
                            :label="`${$t('important_information')}`"
                            class="mb-2"
                            clear-paste-html
                        >
                            <div
                                v-if="selectedLocale"
                                class="fi fill-height px-3"
                                :class="[`fi-${useFlagIconCode(selectedLocale)}`]"
                            />
                        </NewHtmlEditor>
                    </v-col>
                    <template v-if="serviceType === 'customComplexTour'">
                        <v-col cols="12" class="mb-2">
                            <NewHtmlEditor
                                ref="editorIncludedInfoDescription"
                                v-model="includedInfoLocalized"
                                :label="`${$t('included')}`"
                                class="mb-2"
                            >
                                <div
                                    v-if="selectedLocale"
                                    class="fi fill-height px-3"
                                    :class="[`fi-${useFlagIconCode(selectedLocale)}`]"
                                />
                            </NewHtmlEditor>
                        </v-col>
                        <v-col cols="12" class="mb-2">
                            <NewHtmlEditor
                                ref="editorNotIncludedInfoDescription"
                                v-model="notIncludedInfoLocalized"
                                :label="`${$t('not_included')}`"
                                class="mb-2"
                            >
                                <div
                                    v-if="selectedLocale"
                                    class="fi fill-height px-3"
                                    :class="[`fi-${useFlagIconCode(selectedLocale)}`]"
                                />
                            </NewHtmlEditor>
                        </v-col>
                        <v-col cols="12" class="mb-2">
                            <NewHtmlEditor
                                ref="editorTermsAndCondition"
                                v-model="termsAndConditionLocalized"
                                :label="`${$t('terms_conditions')}`"
                                class="mb-2"
                            >
                                <div
                                    v-if="selectedLocale"
                                    class="fi fill-height px-3"
                                    :class="[`fi-${useFlagIconCode(selectedLocale)}`]"
                                />
                            </NewHtmlEditor>
                        </v-col>
                    </template>
                </v-row>
            </v-col>
        </v-row>
        <v-row no-gutters class="gc-4">
            <v-btn :loading="loadingSaveDescriptions" :disabled="loadingDescriptions" @click="saveDescription">
                {{ $t('save') }}
            </v-btn>
            <v-btn
                class="cancel-btn"
                data-cy="cancel"
                :disabled="loadingDescriptions"
                :to="{
                    name: 'supplierProducts',
                    hash: `#${
                        serviceType === 'ownExcursion' || serviceType === 'concert' ? 'activities' : serviceType
                    }`,
                }"
                variant="text"
            >
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>

<script lang="ts">
    import {Component, Vue, Watch} from 'vue-facing-decorator'
    import {clone} from '@/utils/helpers'

    @Component({
        components: {
            NewHtmlEditor: defineAsyncComponent(() => import('@/components/admin/NewHtmlEditor.vue')),
        },
    })
    export class ProductDescriptionForm extends Vue {
        selectedLocale: string = 'en'

        loadingDescriptions = supplierStore.isLoading
        loadingSaveDescriptions = false

        ownProductContracts = null
        description = null
        descriptionShort = null
        importantInformation = null
        includedInfoDescription = null
        notIncludedInfoDescription = null
        termsAndCondition = null

        isShowEditors = true
        appMethod = ''

        @Watch('selectedLocale')
        async onSelectedLocaleChange() {
            await this.flickEditors()
        }

        async flickEditors() {
            this.isShowEditors = false
            await this.$nextTick
            this.isShowEditors = true
        }

        async mounted() {
            this.selectedLocale = this.$i18n.locale
            await this.getDescriptions()
            //TODO add other products methods after API will exists
            const methods = {
                ownExcursion: 'ownExcursionContracts',
                concert: 'ownEventContracts',
                ownTransfer: 'ownTransferContracts',
                ownCarRent: 'ownCarRentContracts',
                tour: 'ownTourContracts',
                ownCruise: 'ownCruiseContracts',
                ownVisa: 'ownVisaContracts',
                ownExtraService: 'ownExtraServiceContracts',
                ownInsurance: 'ownInsuranceContracts',
                customComplexTour: 'ownPackageContracts',
            }

            this.appMethod = methods[this.serviceType] || 'contracts'
        }

        get serviceType() {
            return supplierStore.contract.serviceType
        }

        async getDescriptions() {
            this.ownProductContracts = clone(supplierStore.fullContract)
            if (this.serviceType !== 'ownVisa') {
                this.description = this.ownProductContracts?.description || {}
                this.descriptionShort = this.ownProductContracts?.shortDescription || {}
                this.importantInformation = this.ownProductContracts?.importantInformation || {}
            }
            if (this.serviceType === 'ownVisa') {
                this.description = this.ownProductContracts?.description || {}
            }
            if (this.serviceType === 'customComplexTour') {
                this.includedInfoDescription = this.ownProductContracts?.includedInfoDescription || {}
                this.notIncludedInfoDescription = this.ownProductContracts?.notIncludedInfoDescription || {}
                this.termsAndCondition = this.ownProductContracts?.termsAndCondition || {}
            }
        }

        async saveDescription() {
            let rq = {
                ...this.ownProductContracts,
                description: this.description,
                shortDescription: this.descriptionShort,
            }
            if (this.serviceType !== 'customComplexTour' && this.serviceType !== 'ownVisa') {
                rq = {
                    ...rq,
                    importantInformation: this.importantInformation,
                }
            }
            if (this.serviceType === 'ownVisa') {
                rq = {
                    ...this.ownProductContracts,
                    description: this.description,
                }
            }
            if (this.serviceType === 'customComplexTour') {
                rq = {
                    ...rq,
                    includedInfoDescription: this.includedInfoDescription,
                    notIncludedInfoDescription: this.notIncludedInfoDescription,
                    termsAndCondition: this.termsAndCondition,
                }
            }
            delete rq.programName
            try {
                this.loadingSaveDescriptions = true
                const {
                    ownProductContracts: [_contract],
                } = await this.$api[this.appMethod].put(rq)
                supplierStore.fullContract = _contract
                this.$toast.success(this.$t('success_message.saved_successfully'))
            } catch (e) {
                this.$toast.error(this.$t('error_message.save_error'))
            } finally {
                this.loadingSaveDescriptions = false
            }
        }

        get descriptionLocalized() {
            return this.description?.[this.selectedLocale] || ''
        }

        set descriptionLocalized(val) {
            this.description[this.selectedLocale] = val
        }

        get descriptionShortLocalized() {
            return this.descriptionShort?.[this.selectedLocale] || ''
        }

        set descriptionShortLocalized(val) {
            this.descriptionShort[this.selectedLocale] = val
        }

        get importantInfoLocalized() {
            return this.importantInformation?.[this.selectedLocale] || ''
        }

        set importantInfoLocalized(val) {
            this.importantInformation[this.selectedLocale] = val
        }

        get includedInfoLocalized() {
            return this.includedInfoDescription?.[this.selectedLocale] || ''
        }

        set includedInfoLocalized(val) {
            this.includedInfoDescription[this.selectedLocale] = val
        }

        get notIncludedInfoLocalized() {
            return this.notIncludedInfoDescription?.[this.selectedLocale] || ''
        }

        set notIncludedInfoLocalized(val) {
            this.notIncludedInfoDescription[this.selectedLocale] = val
        }

        get termsAndConditionLocalized() {
            return this.termsAndCondition?.[this.selectedLocale] || ''
        }

        set termsAndConditionLocalized(val) {
            this.termsAndCondition[this.selectedLocale] = val
        }

        changeActiveLangCode(code): void {
            this.selectedLocale = code
        }
    }

    export default ProductDescriptionForm
</script>
